import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { withStyleSheet } from 'hoc/styles';
import { ContentTabs } from 'components/ContentTabs';
import { Div } from 'components/Div';
import { ErrorBoundary } from 'components/ErrorBoundary';
import { Icon } from 'components/Icon';
import { LoaderLogo } from 'components/LoaderLogo';
import { Text } from 'components/Text';
import { TextInput } from 'components/TextInput';
import { PERMISSIONS } from 'consts';
import { get } from 'utils';
import { EventHighlights } from '../EventHighlights';
import { EventLocalMonitors } from '../EventLocalMonitors';
import { EventMentions } from '../EventMentions';
import { styleSheet } from './stylesheet';
import { EventAskAiera } from '../EventAskAiera';
import { EventMatch } from '../EventMatch';

class EventSidebar extends PureComponent {
    static propTypes = {
        collapsedDashboards: PropTypes.arrayOf(PropTypes.any).isRequired,
        eventId: PropTypes.string,
        equityId: PropTypes.string,
        highlightsFilterKey: PropTypes.string.isRequired,
        highlightsSortKey: PropTypes.string.isRequired,
        isPublic: PropTypes.bool.isRequired,
        keyMentions: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
        loading: PropTypes.bool.isRequired,
        localMonitorMatches: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
        monitorMatches: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
        onNodeSelect: PropTypes.func.isRequired,
        onSearch: PropTypes.func.isRequired,
        onSearchFocus: PropTypes.func.isRequired,
        onTermSelect: PropTypes.func.isRequired,
        pageId: PropTypes.string,
        passedStyles: PropTypes.objectOf(PropTypes.any),
        scrollRef: PropTypes.objectOf(PropTypes.any).isRequired,
        searchResults: PropTypes.arrayOf(PropTypes.any),
        searchTerm: PropTypes.string,
        selectTab: PropTypes.func.isRequired,
        selectedNode: PropTypes.string,
        selectedTab: PropTypes.string.isRequired,
        selectedTerm: PropTypes.objectOf(PropTypes.any),
        setHighlightsFilter: PropTypes.func.isRequired,
        setHighlightsSort: PropTypes.func.isRequired,
        shareBookmarks: PropTypes.bool.isRequired,
        styles: PropTypes.objectOf(PropTypes.any).isRequired,
        theme: PropTypes.objectOf(PropTypes.any).isRequired,
        toggleDash: PropTypes.func.isRequired
    };

    static defaultProps = {
        eventId: null,
        equityId: null,
        keyMentions: [],
        localMonitorMatches: [],
        monitorMatches: [],
        pageId: null,
        passedStyles: {},
        searchResults: [],
        searchTerm: '',
        selectedNode: null,
        selectedTerm: null
    };

    renderHeader() {
        const { onSearch, onSearchFocus, searchTerm, styles, selectTab, selectedTab } = this.props;

        return (
            <Div styles={styles.header}>
                <TextInput
                    name="eventTranscriptSearch"
                    onChange={onSearch}
                    value={searchTerm}
                    styles={styles.searchBox}
                    icon="search03"
                    placeholder="Search transcript..."
                    clearable
                    onFocus={onSearchFocus}
                />
                <ContentTabs
                    styles={styles.tabs}
                    onSelect={selectTab}
                    options={[
                        { label: 'Key Insights', value: 'keyMentions' },
                        {
                            label: 'Highlights',
                            value: 'highlights',
                            permissions: [PERMISSIONS.featureBookmarks]
                        }
                    ]}
                    selectedOption={selectedTab}
                />
            </Div>
        );
    }

    renderSelectedTerm() {
        const { selectedTerm, styles, onTermSelect, theme } = this.props;
        const dashboardName = get(selectedTerm, 'dashboardName');
        const name = get(selectedTerm, 'name');
        const matches = get(selectedTerm, 'matches', []);
        const streamId = get(selectedTerm, 'streamId');

        return (
            <ErrorBoundary>
                <Div styles={styles.selectedTermWrapper}>
                    <Div styles={styles.selectedTerm} onClick={() => onTermSelect(undefined, null)}>
                        <Div styles={styles.selectedTermTitle}>
                            <Text styles={styles.selectedTermName} size={3} weight="medium">
                                {name}
                            </Text>
                            <Text className="selectedTermParent" size={0}>
                                {dashboardName}
                            </Text>
                        </Div>
                        <Div className="selectedTermBack" styles={styles.selectedTermBack}>
                            <Icon type="arrowLeft" color={theme.colors.black01} />
                        </Div>
                    </Div>
                </Div>
                {this.renderMatches(matches, streamId)}
            </ErrorBoundary>
        );
    }

    renderMatches(matches, streamId) {
        const { onNodeSelect, searchTerm, selectedNode } = this.props;
        // Matches can be from search results
        // or key mentions / local monitors etc
        if (matches && matches.length > 0) {
            return matches.map((data, index) => {
                const eventId = get(data, 'eventId', get(data, 'id')).toString();
                return (
                    <EventMatch
                        selectedNode={selectedNode}
                        streamId={streamId}
                        searchTerm={searchTerm}
                        onNodeSelect={onNodeSelect}
                        data={data}
                        total={matches.length}
                        index={index + 1}
                        key={eventId}
                    />
                );
            });
        }

        return null;
    }

    renderKeyMentions() {
        const {
            collapsedDashboards,
            equityId,
            eventId,
            isPublic,
            keyMentions,
            localMonitorMatches,
            monitorMatches,
            onTermSelect,
            selectedNode,
            selectedTerm,
            styles,
            toggleDash
        } = this.props;

        if (selectedTerm) {
            return this.renderSelectedTerm();
        }

        return (
            <ErrorBoundary>
                {!isPublic && !!equityId && (
                    <ErrorBoundary>
                        <EventLocalMonitors
                            eventId={eventId}
                            equityId={equityId}
                            onTermSelect={onTermSelect}
                            selectedNode={selectedNode}
                            streamMatches={localMonitorMatches}
                        />
                    </ErrorBoundary>
                )}
                {!isPublic && (
                    <ErrorBoundary>
                        <EventMentions
                            collapsedDashboards={collapsedDashboards}
                            dashboardStreamMatches={monitorMatches}
                            mentionsTitle="Monitor Matches"
                            onTermSelect={onTermSelect}
                            selectedNode={selectedNode}
                            toggleDash={toggleDash}
                        />
                    </ErrorBoundary>
                )}
                <EventMentions
                    onTermSelect={onTermSelect}
                    selectedNode={selectedNode}
                    mentionsTitle="Suggested"
                    streamMatches={keyMentions}
                />
                {(!keyMentions || keyMentions.length === 0) &&
                    (!monitorMatches || monitorMatches.length === 0) &&
                    (!localMonitorMatches || localMonitorMatches.length === 0) && (
                        <Div styles={styles.empty}>
                            <Text size={3} styles={styles.emptyText}>
                                No Key Mentions Found.
                            </Text>
                            <Text size={1} styles={styles.emptyText}>
                                Key terms and phrases are surfaced <br />
                                through your monitors, and determined <br />
                                by our experts and NLP tooling.
                            </Text>
                        </Div>
                    )}
            </ErrorBoundary>
        );
    }

    renderSearchResults() {
        const { searchResults, searchTerm, styles } = this.props;

        if (searchResults && searchResults.length > 0) {
            return this.renderMatches(searchResults);
        }

        return (
            <Div styles={styles.empty}>
                <Text size={3} styles={styles.emptyText}>
                    {`No results for ${searchTerm}`}
                </Text>
            </Div>
        );
    }

    render() {
        const {
            eventId,
            highlightsFilterKey,
            highlightsSortKey,
            keyMentions,
            loading,
            localMonitorMatches,
            monitorMatches,
            onNodeSelect,
            pageId,
            passedStyles,
            scrollRef,
            searchResults,
            selectedTab,
            selectedNode,
            setHighlightsFilter,
            setHighlightsSort,
            shareBookmarks,
            styles
        } = this.props;
        const matchDict = {
            keyMentions: [...keyMentions, ...monitorMatches, ...localMonitorMatches],
            searchResults
        };
        const hasNoMatches =
            (selectedTab === 'keyMentions' || selectedTab === 'searchResults') &&
            (!matchDict[selectedTab] || matchDict[selectedTab].length === 0);
        return (
            <Div styles={{ ...styles.container, ...passedStyles }} className="print-hide">
                {this.renderHeader()}
                {loading && hasNoMatches ? (
                    <Div styles={styles.loader}>
                        <LoaderLogo height={60} />
                    </Div>
                ) : (
                    <Fragment>
                        {selectedTab === 'highlights' ? (
                            <EventHighlights
                                pageId={pageId}
                                audioCallId={eventId}
                                eventId={eventId}
                                filterKey={highlightsFilterKey}
                                onNodeSelect={onNodeSelect}
                                setFilter={setHighlightsFilter}
                                setSort={setHighlightsSort}
                                shareBookmarks={shareBookmarks}
                                sortKey={highlightsSortKey}
                            />
                        ) : (
                            <Div styles={styles.scrollContainer} ref={scrollRef}>
                                {selectedTab === 'ask' && (
                                    <EventAskAiera
                                        eventId={eventId}
                                        onNodeSelect={onNodeSelect}
                                        selectedNode={selectedNode}
                                    />
                                )}
                                {selectedTab === 'keyMentions' && this.renderKeyMentions()}
                                {selectedTab === 'searchResults' && this.renderSearchResults()}
                            </Div>
                        )}
                    </Fragment>
                )}
            </Div>
        );
    }
}

export const EventSidebarUI = compose(withStyleSheet(styleSheet))(EventSidebar);
